import React, { useState } from 'react';
import { Box, Divider, FormControlLabel, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import StyledButton from '../Common/StyledButton';
import StyledSwitch from '../Common/StyledSwitch';
import { useAppSelector } from '../../redux/hooks';
import {
  ICreatePrivacySettingsPayload,
  createClientPrivacySettings,
  setOpenUpdatePrivacySettingsConcentModal,
  updateClientPrivacySettings,
} from '../../redux/client/clientSlice';
import RecordingDisclaimerModal from '../SingleClient/RecordingDisclaimerModal/RecordingDisclaimerModal';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { StyledCheckbox } from '../Onboarding/Details';

export const StyledFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  width: '100%',
  justifyContent: 'space-between',
  marginBottom: '16px',
  '& .MuiFormControlLabel-label.Mui-disabled': {
    color: 'silver',
  },
});

export default function PrivacySettings() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    privacySettings,
    selectedClient,
    updatePrivacySettingsLoading,
    openUpdatePrivacySettingsConcentModal,
    createPrivacySettingsLoading,
    openPrivacySettingsConcentModal,
  } = useAppSelector((state) => state.client);
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const coachAISettings = useAppSelector((state) => state.coach.coachAISettings);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const validationSchema = Yup.object().shape({
    magicSuggestionsEnabled: Yup.boolean().required(),
    noteAssistEnabled: Yup.boolean().required(),
  });

  const formik = useFormik({
    initialValues:
      privacySettings !== null
        ? privacySettings
        : { id: '', noteAssistEnabled: false, magicSuggestionsEnabled: false },
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => {
      dispatch(setOpenUpdatePrivacySettingsConcentModal(true));
    },
  });
  const [isAgreeBtn, setIsAgreeBtn] = useState(false);
  const [isNoteAssistEnabled, setIsNoteAssistEnabled] = useState(true);
  const [isMagicSuggestionsEnabled, setIsMagicSuggestionsEnabled] = useState(true);

  const handleUpdatePrivacySettings = () => {
    if (selectedClient) {
      const { magicSuggestionsEnabled, noteAssistEnabled, id } = formik.values;
      dispatch(
        updateClientPrivacySettings({
          clientId: selectedClient?.id,
          concentId: id,
          coachId,
          magicSuggestionsEnabled,
          noteAssistEnabled,
        })
      );
    }
  };

  const handleAgreeConcent = () => {
    if (selectedClient) {
      const payload: ICreatePrivacySettingsPayload = {
        coachId,
        clientId: selectedClient?.id,
        noteAssistEnabled: isNoteAssistEnabled,
        magicSuggestionsEnabled: isMagicSuggestionsEnabled,
      };
      setIsAgreeBtn(true);
      dispatch(createClientPrivacySettings(payload));
    }
  };

  const handleDisagreeConcent = () => {
    if (selectedClient) {
      const payload: ICreatePrivacySettingsPayload = {
        coachId,
        clientId: selectedClient?.id,
        noteAssistEnabled: false,
        magicSuggestionsEnabled: false,
      };
      setIsAgreeBtn(false);
      dispatch(createClientPrivacySettings(payload));
    }
  };

  return (
    <Grid
      container
      p={3}
      borderRadius="10px"
      boxShadow="0px 4px 40px 0px rgba(255, 181, 171, 0.20)"
      bgcolor="#FFF"
    >
      <Grid item xs={12}>
        <Typography variant="bodyBold" color="primary.main">
          {t('clientPrivacySettings.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} mt={4.5} mb={3}>
        <Typography variant="bodyRegular" color="primary.main">
          {t('clientPrivacySettings.description')}
        </Typography>
      </Grid>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container xs={12}>
          <Grid item xs={8}>
            <StyledFormControlLabel
              control={
                <StyledSwitch
                  name="magicSuggestionsEnabled"
                  checked={
                    coachAISettings.isMagicSuggestionEnabled === false
                      ? coachAISettings.isMagicSuggestionEnabled
                      : formik.values.magicSuggestionsEnabled
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              }
              disabled={
                !coachAISettings.isMagicSuggestionEnabled ||
                !checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_CONCENT)
              }
              label={t('clientPrivacySettings.magicSuggestions')}
              labelPlacement="start"
            />
          </Grid>
          <Grid item xs={8}>
            <StyledFormControlLabel
              control={
                <StyledSwitch
                  name="noteAssistEnabled"
                  checked={
                    coachAISettings.isNoteTakingAIEnabled === false
                      ? coachAISettings.isNoteTakingAIEnabled
                      : formik.values.noteAssistEnabled
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              }
              disabled={
                !coachAISettings.isNoteTakingAIEnabled ||
                !checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_CONCENT)
              }
              label={t('clientPrivacySettings.assistedNotes')}
              labelPlacement="start"
            />
          </Grid>

          {checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_CONCENT) && (
            <Grid item xs={12} mt={4.5} mb={2.5}>
              <Divider sx={{ color: '#EEEEEE' }} />
            </Grid>
          )}

          {checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_CONCENT) && (
            <Grid item xs={12} display="flex" justifyContent="end">
              <StyledButton
                disabled={
                  !privacySettings.id ||
                  (!coachAISettings.isMagicSuggestionEnabled &&
                    !coachAISettings.isNoteTakingAIEnabled)
                }
              >
                {t('common.update')}
              </StyledButton>
            </Grid>
          )}
        </Grid>
      </form>
      {checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_CONCENT) && (
        <RecordingDisclaimerModal
          handleOnClickLeft={() => handleDisagreeConcent()}
          handleOnClickRight={() => handleAgreeConcent()}
          content={
            <>
              <Typography variant="bodyRegular">
                {t('notes.recordingDisclaimer.createMessage.section1')}
              </Typography>
              <br />
              <br />
              <Typography variant="bodyRegular">
                {t('notes.recordingDisclaimer.createMessage.section2')}
              </Typography>
              <br />
              <br />
              <Grid container justifyContent="center">
                {coachAISettings.isMagicSuggestionEnabled && (
                  <Grid item xs={5} display="flex" alignItems="center">
                    <StyledCheckbox
                      checked={isMagicSuggestionsEnabled}
                      onChange={() => setIsMagicSuggestionsEnabled(!isMagicSuggestionsEnabled)}
                      inputProps={{ 'aria-label': 'controlled' }}
                      checkedIcon={
                        <Box component="img" src="/assets/icons/check-box-checked.svg" />
                      }
                    />
                    <Typography variant="bodyRegular">
                      {t('notes.recordingDisclaimer.createMessage.magicSuggestions')}
                    </Typography>
                  </Grid>
                )}
                {coachAISettings.isNoteTakingAIEnabled && (
                  <Grid item xs={5} display="flex" alignItems="center">
                    <StyledCheckbox
                      checked={isNoteAssistEnabled}
                      onChange={() => setIsNoteAssistEnabled(!isNoteAssistEnabled)}
                      inputProps={{ 'aria-label': 'controlled' }}
                      checkedIcon={
                        <Box component="img" src="/assets/icons/check-box-checked.svg" />
                      }
                    />
                    <Typography variant="bodyRegular">
                      {t('notes.recordingDisclaimer.createMessage.assistedNotes')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          }
          btnTextLeft={t('common.disagree')}
          btnTextRight={t('common.agree')}
          btnLeftLoading={createPrivacySettingsLoading && !isAgreeBtn}
          btnRightLoading={createPrivacySettingsLoading && isAgreeBtn}
          open={
            openPrivacySettingsConcentModal &&
            (coachAISettings.isMagicSuggestionEnabled || coachAISettings.isNoteTakingAIEnabled)
          }
        />
      )}

      <RecordingDisclaimerModal
        handleOnClickLeft={() => {
          dispatch(setOpenUpdatePrivacySettingsConcentModal(false));
        }}
        handleOnClickRight={handleUpdatePrivacySettings}
        content={
          <>
            <Typography variant="bodyRegular">
              {t('notes.recordingDisclaimer.createMessage.section1')}
            </Typography>
            <br />
            <br />
            <Typography variant="bodyRegular">
              {t('notes.recordingDisclaimer.updateMessage.section2')}
            </Typography>
          </>
        }
        btnTextLeft={t('common.cancel')}
        btnTextRight={t('common.continue')}
        btnRightLoading={updatePrivacySettingsLoading}
        open={openUpdatePrivacySettingsConcentModal}
      />
    </Grid>
  );
}
